import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Layers,
  Terminal,
  Activity,
  GitBranch,
  Bot,
  Cpu,
  BarChart3,
  Sliders,
  Search,
  RefreshCw,
  CreditCard,
  ClipboardList,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
  LogOut,
  ShieldCheck,
  Clock,
  Radio,
  Webhook,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/useNotifications';
import { useToast } from '../contexts/ToastContext';
import { API_BASE_URL, TENANT_ID } from '../utils/api';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  exact?: boolean;
  badgeCount?: number;
}


interface NavGroup {
  id: string;
  title: string;
  items: NavItem[];
}

const SIDEBAR_STORAGE_KEY = 'admin_sidebar_collapsed';

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const { connected: rtConnected, notifications } = useNotifications();
  const { addToast } = useToast();

  const [jobEventCount, setJobEventCount] = useState(0);
  const [uptimeSeconds, setUptimeSeconds] = useState<number | null>(null);
  const [unreadLogsCount, setUnreadLogsCount] = useState<number>(0);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const lastJobEvent = useMemo(() => notifications.find((n) => n.type?.startsWith('job:')), [notifications]);

  useEffect(() => {
    if (lastJobEvent) {
      setJobEventCount((c) => c + 1);
      const q = lastJobEvent.payload?.queueName || 'queue';
      const id = lastJobEvent.payload?.jobId || '';
      addToast({
        type: lastJobEvent.type === 'job:failed' ? 'error' : 'info',
        title: 'Evento de Job',
        message: `${lastJobEvent.type} - ${q} ${id ? `#${id}` : ''}`,
        duration: 3500,
      });
    }
  }, [lastJobEvent, addToast]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  const toggleSidebarCollapse = () => {
    setIsCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
      } catch {
        // Ignorar errores de almacenamiento restringido
      }
      return next;
    });
  };

  // Fetch real system uptime and unread debug logs count
  useEffect(() => {
    let cancelled = false;

    const fetchSystemStatus = async () => {
      if (!token) return;
      try {
        const headers: HeadersInit = {
          Authorization: `Bearer ${token}`,
          ...(TENANT_ID ? { 'x-tenant-id': TENANT_ID } : {}),
        };

        const [statsRes, logsRes] = await Promise.allSettled([
          fetch(`${API_BASE_URL}/admin/monitoring/stats`, { headers }),
          fetch(`${API_BASE_URL}/admin/debug-logs/unread-count`, { headers }),
        ]);

        if (!cancelled && statsRes.status === 'fulfilled' && statsRes.value.ok) {
          const statsData = await statsRes.value.json();
          if (typeof statsData.uptime === 'number') {
            setUptimeSeconds(statsData.uptime);
          }
        }

        if (!cancelled && logsRes.status === 'fulfilled' && logsRes.value.ok) {
          const logsData = await logsRes.value.json();
          if (typeof logsData.unreadCount === 'number') {
            setUnreadLogsCount(logsData.unreadCount);
          }
        }
      } catch {
        // Ignorar fallos de red silenciosos en background status
      }
    };

    void fetchSystemStatus();
    const interval = setInterval(fetchSystemStatus, 30000);

    const handleDebugLogsUpdated = () => {
      void fetchSystemStatus();
    };
    window.addEventListener('debug-logs:updated', handleDebugLogsUpdated);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('debug-logs:updated', handleDebugLogsUpdated);
    };
  }, [token]);

  const isActive = (path: string, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navGroups: NavGroup[] = useMemo(
    () => [
      {
        id: 'ops',
        title: 'Operación y Salud',
        items: [
          { to: '/admin', label: 'Overview', icon: LayoutDashboard, exact: true },
          { to: '/admin/nodes', label: 'Nodos y Proxies', icon: Radio },
          { to: '/admin/jobs', label: 'Jobs', icon: Layers },
          { to: '/admin/debug-logs', label: 'Logs de Debug', icon: Terminal, badgeCount: unreadLogsCount },
          { to: '/admin/webhooks', label: 'Inspector Webhooks', icon: Webhook },
          { to: '/admin/diagnostics', label: 'Diagnóstico', icon: Activity },
        ],
      },
      {
        id: 'observability',
        title: 'Observabilidad',
        items: [
          { to: '/admin/debug', label: 'Flow Debugger', icon: GitBranch },
          { to: '/admin/search-log', label: 'SearchLog', icon: Search },
          { to: '/admin/catalog-sync', label: 'Catalog Sync', icon: RefreshCw },
        ],
      },
      {
        id: 'ai',
        title: 'Inteligencia Artificial',
        items: [
          { to: '/admin/ai-providers', label: 'Proveedores IA (Chat)', icon: Bot },
          { to: '/admin/embedding-providers', label: 'Proveedores Embeddings', icon: Cpu },
          { to: '/admin/ai-usage', label: 'Uso de IA', icon: BarChart3 },
        ],
      },
      {
        id: 'platform',
        title: 'Plataforma',
        items: [
          { to: '/admin/feature-flags', label: 'Feature Flags', icon: Sliders },
          { to: '/admin/plans', label: 'Planes y Precios', icon: CreditCard },
          { to: '/admin/beta-interest', label: 'Reservas Beta', icon: ClipboardList },
        ],
      },
    ],
    [unreadLogsCount]
  );

  return (
    <div className="dashboard-layout">
      <header className="dashboard-header">
        <div className="header-left">
          <button
            type="button"
            className="sidebar-mobile-trigger"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label={mobileMenuOpen ? 'Cerrar menú de navegación' : 'Abrir menú de navegación'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={toggleSidebarCollapse}
            aria-label={isCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
            title={isCollapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral'}
          >
            {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>

          <img
            src="/new/IridiaChat_Logo_Fondo_Claro_Transparente.png"
            alt="IridiaChat"
            className="header-logo"
          />

          <div className="header-title-block">
            <h1>Panel de Administración del Sistema</h1>
            <div className="connection-status">
              <span
                className={`status-indicator ${rtConnected ? 'connected' : 'disconnected'}`}
                title="Estado del canal Socket.IO"
              >
                <span className="status-dot" aria-hidden="true" />
                {rtConnected ? 'Tiempo real conectado' : 'Tiempo real desconectado'}
              </span>
              {jobEventCount > 0 && <span className="badge">{jobEventCount} eventos</span>}
            </div>
          </div>
        </div>

        <div className="header-right">
          <div className="header-uptime-pill" title="Tiempo activo del servidor">
            <Clock size={14} />
            <span>Uptime: {formatRealUptime(uptimeSeconds)}</span>
          </div>

          <div className="user-info-pill">
            <ShieldCheck size={15} />
            <span className="user-info">{user?.username || 'Admin'}</span>
          </div>

          <button type="button" onClick={logout} className="logout-button">
            <LogOut size={15} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {mobileMenuOpen && (
          <div
            className="sidebar-backdrop"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <nav
          className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}
          aria-label="Navegación de administración"
        >
          <div className="sidebar-scroll-area">
            {navGroups.map((group) => (
              <div key={group.id} className="nav-group">
                {!isCollapsed && <div className="nav-group-title">{group.title}</div>}
                <ul className="nav-menu">
                  {group.items.map((item) => {
                    const IconComponent = item.icon;
                    const active = isActive(item.to, item.exact);
                    return (
                      <li key={item.to}>
                        <Link
                          to={item.to}
                          className={`nav-link ${active ? 'active' : ''}`}
                          title={isCollapsed ? item.label : undefined}
                        >
                          <span className="nav-link-icon">
                            <IconComponent size={18} />
                          </span>
                          {!isCollapsed && <span className="nav-link-label">{item.label}</span>}
                          {typeof item.badgeCount === 'number' && item.badgeCount > 0 && (
                            <span
                              className={`nav-badge ${isCollapsed ? 'collapsed-badge' : ''}`}
                              aria-label={`${item.badgeCount} sin leer`}
                            >
                              {item.badgeCount > 99 ? '99+' : item.badgeCount}
                            </span>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>

          <div className="sidebar-footer">
            {!isCollapsed ? (
              <div className="system-info">
                <h4>Sistema</h4>
                <p>Versión: 1.0.0</p>
                <p>Uptime: {formatRealUptime(uptimeSeconds)}</p>
              </div>
            ) : (
              <div className="system-info-collapsed" title={`Sistema v1.0.0 • Uptime: ${formatRealUptime(uptimeSeconds)}`}>
                <span>v1.0</span>
              </div>
            )}
          </div>
        </nav>

        <main className="main-content">{children}</main>
      </div>
    </div>
  );
};

const formatRealUptime = (seconds: number | null) => {
  if (seconds === null || seconds < 0) return 'Conectando...';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

export default DashboardLayout;

